import { extractApiError } from '@/originator/statusUtils';

/**
 * Which surface a failed advisor submit gets.
 *
 * `advisorActionable` carries the i18n key of a specific, self-service instruction — the
 * advisor can finish the submit themselves once they act on it, so it stays a toast and the
 * GAMX support contact would be noise. `technical` is everything else: the press failed for a
 * reason the advisor cannot resolve, and the requirement is that those show the support block.
 */
export type SubmitFailureSurface =
  | { kind: 'advisorActionable'; messageKey: string }
  | { kind: 'sessionFault' }
  | { kind: 'technical' };

/**
 * Authentication faults, which get the "sign in again" dialog rather than the support block.
 *
 * <p>Keyed on the CODE, deliberately not on the 503 status they share. Three codes answer 503
 * and only two of them are authentication: `BMO_NOT_CONFIGURED` is a missing base URL — a
 * deployment defect. Routing that one here would send the advisor to sign in, let them sign in
 * successfully, fail identically on retry, and loop; and the genuine platform fault would never
 * reach the support block or raise an alert.</p>
 *
 * <p>This is the same pair the BFF excludes from support alerting
 * (`IncidentDescriptions.ShouldRaiseForBmoFailure`), and that is the point: an expired session
 * is not worth waking BMO's on-call over, so it must not tell the advisor to phone them either.
 * If one list changes, the other should.</p>
 */
const SESSION_FAULT_CODES: ReadonlySet<string> = new Set([
  // BMO refused the advisor's ISAM token — expired or rotated.
  'BMO_TOKEN_REJECTED',
  // The request reached the BFF with no usable ISAM token to send onward.
  'BMO_NO_ACCESS_TOKEN',
]);

/**
 * The allow-list, and it is deliberately an allow-list rather than a list of "technical" codes.
 *
 * An unrecognised failure is treated as technical, so a code added on the BFF tomorrow lands on
 * the surface that hands the advisor a phone number instead of a dead-end "please try again"
 * with nowhere to go. Getting that default backwards is the failure mode worth designing out.
 *
 * Only one condition qualifies today. Every other 4xx the submit endpoint raises is a workflow
 * or platform defect rather than something the advisor can act on:
 *   - NOT_READY_TO_SUBMIT  — BMO's status ladder disagrees with ours; not advisor-fixable.
 *   - NO_DOCUMENTS_TO_SUBMIT, INVALID_GENERATED_PDF, INVALID_SUPPRESSED_SIGNATURE,
 *     INVALID_ESIGN_SIGNER, INVALID_SUPPORTING_DOC — all authoring defects in the published
 *     workflow. The advisor cannot repair a template from the form.
 *   - INVALID_APPLICATION_ID — a malformed id the advisor never typed.
 */
const ADVISOR_ACTIONABLE: Readonly<Record<string, string>> = {
  // Still scanning or converting. Waiting is the fix, and the existing copy says exactly that.
  FILES_NOT_READY: 'formRenderer.pdfGeneration.filesNotReady',
};

/**
 * Classifies a thrown submit failure.
 *
 * BFF session expiry never reaches here — ADR-0022's warning-modal/redirect flow owns that
 * screen, and `submitApplication` filters `ApplicationSessionExpiredError` out before calling
 * in. The `sessionFault` kind below is a DIFFERENT condition: our BFF session is alive and well,
 * and it is the ISAM token behind it that BMO would not take. Nothing redirects on its own for
 * that, which is exactly why it needs a surface of its own rather than silence.
 */
export function classifySubmitFailure(err: unknown): SubmitFailureSurface {
  const { code } = extractApiError(err) as { code?: string };
  if (code && SESSION_FAULT_CODES.has(code)) return { kind: 'sessionFault' };
  const messageKey = code ? ADVISOR_ACTIONABLE[code] : undefined;
  return messageKey ? { kind: 'advisorActionable', messageKey } : { kind: 'technical' };
}
